import React, { ReactElement, useEffect, useState } from 'react';
import axios from 'axios';
import { Deferred, router } from '@inertiajs/react';
import {
  Box,
  CardHeader,
  ClickAwayListener,
  IconButton,
  Menu,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTheme } from '@mui/material/styles';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CircularProgress from '@mui/material/CircularProgress';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { isBefore } from 'date-fns';
import Badge from '@mui/material/Badge';

import ActivitySettings from './ActivitySettings';

import { ActivityLoadingSkeleton } from '@/pages/activity/ActivityLoadingSkeleton';
import { Activity } from '@/types';
import {
  getFirstActivityAfterDatePid,
  groupActivities,
} from '@/pages/activity/utils';
import { MixpanelEventName, MixpanelUtils } from '@/mixpanel';
import { timestampToRelativeTimeFromNow } from '@/core/utils';
import { ActivityItem } from '@/pages/activity/ActivityItem';
import { useFlashMessage } from '@/core/hooks/FlashMessageContext';
import { useBoolean } from '@/core/hooks/useBoolean';
import { Divider } from '@/core/ui/Divider';

type Props = {
  activities: Activity[];
  areAllActivitiesLoaded: boolean;
  activitiesViewedAt: Date | null;
  hasUnviewedActivities: number;
};

export const ActivityMenu = ({
  activities,
  areAllActivitiesLoaded,
  activitiesViewedAt,
  hasUnviewedActivities,
}: Props): ReactElement => {
  const theme = useTheme();
  const { showFlashMessage } = useFlashMessage();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [page, setPage] = useState(1);
  const [openSettings, toggleSettings] = useBoolean(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [allActivitiesLoaded, setAllActivitiesLoaded] = useState(
    areAllActivitiesLoaded,
  );
  const [fetchedActivities, setFetchedActivities] = useState<Activity[]>([]);

  const reloadActivities = (): void => {
    router.reload({
      only: [
        'activities',
        'auth',
        'areAllActivitiesLoaded',
        'hasUnviewedActivities',
      ],
      onSuccess: (currentPage) => {
        setPage(1);
        const areAllLoaded = currentPage.props
          .areAllActivitiesLoaded as boolean;
        setAllActivitiesLoaded(areAllLoaded);
        setFetchedActivities(currentPage.props.activities as Activity[]);
      },
    });
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>): void => {
    reloadActivities();
    MixpanelUtils.track(
      MixpanelEventName.portalAdvisorNotificationCenterClick,
      {},
    );
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = async (): Promise<void> => {
    setAnchorEl(null);
    await axios.post(route('activities.update-viewed'));
    reloadActivities();
  };

  const handleClickAway = (): void => {
    if (anchorEl) {
      handleCloseMenu();
    }
  };

  const handleLoadMore = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        route('activities.load-more', {
          page: page + 1,
        }),
      );

      const newActivities = response.data.activities.data || [];
      const currentPage = response.data.activities.current_page;
      const lastPage = response.data.activities.last_page;
      if (newActivities.length > 0) {
        setFetchedActivities((prev) => {
          const merged = [...prev, ...newActivities];
          const unique = Array.from(
            new Map(merged.map((a) => [a.pid, a])).values(),
          );
          return unique;
        });
        setPage((prev) => prev + 1);
        setAllActivitiesLoaded(currentPage === lastPage);
      }
    } catch (error) {
      showFlashMessage('Failed to load more activities', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnSettingChange = (): void => {
    router.reload({
      only: [
        'activities',
        'auth',
        'areAllActivitiesLoaded',
        'hasUnviewedActivities',
      ],
      onStart: () => {
        setIsReloading(true);
        setFetchedActivities([]);
      },
      onSuccess: (currentPage) => {
        setPage(1);
        const areAllLoaded = currentPage.props
          .areAllActivitiesLoaded as boolean;
        setAllActivitiesLoaded(areAllLoaded);
      },
      onFinish: () => setIsReloading(false),
    });
  };

  const groupedActivities = groupActivities(fetchedActivities);
  const lastActivityBeforeDividerPid = getFirstActivityAfterDatePid(
    activitiesViewedAt,
    fetchedActivities,
  );

  useEffect(() => {
    setFetchedActivities(activities);
  }, [activities]);

  useEffect(() => {
    setAllActivitiesLoaded(areAllActivitiesLoaded);
  }, [areAllActivitiesLoaded]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ flexGrow: 0 }}>
        <IconButton onClick={handleOpenMenu} sx={{ p: 0 }}>
          <Badge
            badgeContent={hasUnviewedActivities ? ' ' : undefined}
            color="primary"
            sx={{ '> svg': { width: 32, height: 32 } }}
            max={99}
            overlap="circular"
            variant="dot"
            invisible={!hasUnviewedActivities}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <Menu
          sx={{ mt: '45px' }}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={!!anchorEl}
          onClose={handleCloseMenu}
        >
          <CardHeader
            title={
              openSettings ? (
                <Stack
                  sx={{
                    flexDirection: 'row',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    onClick={(): void => {
                      reloadActivities();
                      toggleSettings();
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  Notification Settings
                </Stack>
              ) : (
                <>Notifications</>
              )
            }
            sx={{ px: 3, pt: 2 }}
            action={
              !openSettings ? (
                <IconButton onClick={(): void => toggleSettings()}>
                  <SettingsIcon />
                </IconButton>
              ) : null
            }
            slotProps={{
              title: { variant: 'h6' },
            }}
          />
          {openSettings ? (
            <ActivitySettings onSettingChange={handleOnSettingChange} />
          ) : (
            <Deferred
              data="activities"
              fallback={
                <>
                  <ActivityLoadingSkeleton />
                </>
              }
            >
              <Stack
                sx={{
                  px: 2,

                  minWidth: {
                    lg: 600,
                  },
                }}
              >
                {isReloading ? (
                  <>
                    <ActivityLoadingSkeleton />
                  </>
                ) : groupedActivities.length ? (
                  <>
                    {groupedActivities.map((group, index) => (
                      <Stack
                        key={index}
                        sx={{
                          pb: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ pb: 1, px: 1, fontSize: 12 }}
                        >
                          {timestampToRelativeTimeFromNow(
                            new Date(group[0].createdAt),
                          )}
                        </Typography>
                        {group.map((activity) => (
                          <div key={activity.pid}>
                            <ActivityItem
                              key={activity.createdAt}
                              activity={activity}
                              isHighlighted={
                                !activitiesViewedAt ||
                                isBefore(
                                  activitiesViewedAt,
                                  new Date(activity.createdAt),
                                )
                              }
                            />
                            {lastActivityBeforeDividerPid === activity.pid && (
                              <Stack
                                sx={{
                                  mt: 1,
                                  mb: 1,
                                }}
                              >
                                <Divider text="OLDER NOTIFICATIONS" />
                              </Stack>
                            )}
                          </div>
                        ))}
                      </Stack>
                    ))}
                    {isLoading && (
                      <CircularProgress
                        size="1.3rem"
                        sx={{
                          color: theme.palette.secondary.dark,
                          ml: 1,
                          mb: 0.5,
                        }}
                      />
                    )}
                    {!isLoading && !allActivitiesLoaded && (
                      <Stack
                        onClick={handleLoadMore}
                        sx={{
                          cursor: 'pointer',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            pb: 1,
                            px: 1,
                            fontSize: 12,
                          }}
                        >
                          Show more
                        </Typography>
                      </Stack>
                    )}
                  </>
                ) : (
                  <>
                    <Stack direction="column">
                      <Stack
                        sx={{
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <NotificationsNoneIcon
                          sx={{
                            color: theme.palette.info.main,
                            fontSize: 48,
                          }}
                        />
                      </Stack>
                      <Stack
                        sx={{
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          No notifications, yet!
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ pb: 1 }}
                        >
                          Client activity for the teams you’re a part of will
                          show up here.
                        </Typography>
                      </Stack>
                    </Stack>
                  </>
                )}
              </Stack>
            </Deferred>
          )}
        </Menu>
      </Box>
    </ClickAwayListener>
  );
};
